import React, { Suspense } from 'react'
import { Switch, Route, matchPath, useHistory, useLocation } from 'react-router-dom'

import './assets/scss/style.scss'

import { ResultsPage } from './pages/results'
import { ScheduleCall } from './pages/scheduleCall'
import { CallScheduled } from './pages/callScheduled'
import { PATH_URL } from './helpers'
import { initialQuizPath } from './helpers/quizEntry'
import { useAppBase } from './helpers/app'
import { LoaderFullScreen, ProposalLoadingInterstitial } from './common'
import { CustomerExperiencePage } from './pages/customerExperience'
import { MoodBoard } from './pages/moodboard'
import { CallScheduler } from './pages/callScheduler'
import { PostMoodboard } from './pages/postMoodboard'
import { QuizQuestion } from './pages/quiz/QuizQuestionProvider'
import { SessionError } from './pages/quiz/components/SessionError/SessionError'
import { useSegmentPage } from './helpers/hooks/useSegmentPage'

const BudgetPlannerPage = React.lazy(
  () => import(/* webpackChunkName: "budget-planner" */ './pages/budgetPlanner/BudgetPlannerPage')
)

const QuizBootstrap: React.FC = () => {
  const { isLoading, isLoadingTestimonial, hasSessionError, proposalLoadingStage } = useAppBase()

  return (
    <>
      {hasSessionError && <SessionError />}
      {isLoading && <LoaderFullScreen />}
      {isLoadingTestimonial && <ProposalLoadingInterstitial stage={proposalLoadingStage} />}
    </>
  )
}

const AppBase: React.FC = () => {
  const history = useHistory()
  const { pathname } = useLocation()
  const isBudgetPlanner = !!matchPath(pathname, { path: PATH_URL.BUDGET_PLANNER })

  if (history.location.pathname === '/') {
    console.log('redirecting')
    const currentParams = new URLSearchParams(window.location.search)

    const redirectTo = (baseUrl: string, additionalParams: Record<string, string> = {}, delay = 0): void => {
      const url = new URL(baseUrl, window.location.origin)
      currentParams.forEach((value, key) => {
        if (!additionalParams.hasOwnProperty(key)) {
          url.searchParams.append(key, value)
        }
      })
      for (const [key, value] of Object.entries(additionalParams)) {
        url.searchParams.set(key, value)
      }

      if (delay > 0) {
        console.log(`Delaying redirect to ${baseUrl} for ${delay}ms to allow setting variables`)
        setTimeout(() => {
          location.replace(url.toString())
        }, delay)
      } else {
        location.replace(url.toString())
      }
    }

    // Add a 1-second delay when redirecting to the first quiz page to allow time for setting variables.
    // `?start=date` opens on availability; every other visit still opens on color palettes.
    redirectTo(initialQuizPath(window.location.search), {}, 1000)
  }

  useSegmentPage()
  return (
    <>
      {!isBudgetPlanner && <QuizBootstrap />}
      <Switch>
        <Route path={PATH_URL.BUDGET_PLANNER}>
          <Suspense fallback={null}>
            <BudgetPlannerPage />
          </Suspense>
        </Route>
        <Route path={PATH_URL.YOUR_POPPY_PROPOSAL} component={ResultsPage} />
        <Route path={PATH_URL.CALL_SCHEDULED} component={CallScheduled} />
        <Route path={PATH_URL.$500_CUSTOMER} component={CustomerExperiencePage} />
        <Route path={PATH_URL.SCHEDULE_YOUR_CONSULTATION} component={ScheduleCall} />
        <Route path={PATH_URL.MOODBOARD} component={MoodBoard} />
        <Route path={PATH_URL.POST_MOODBOARD} component={PostMoodboard} />
        <Route path={PATH_URL.SCHEDULE_A_CALL} component={CallScheduler} />
        <Route path={PATH_URL.SWIPING} component={QuizQuestion} />
        <Route path="*" component={QuizQuestion} />
      </Switch>
    </>
  )
}

export default AppBase
